'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { StorageKey } from '@/features/webengage/types';
type Props = {
  prevPath: string | null;
  pageTitle: string | null;
  pathname: string | null;
};
function buildCanonicalPath(pathname: string | null, searchParams: ReturnType<typeof useSearchParams>): string | null {
  if (!pathname) return null;
  if (pathname !== '/search') return pathname;
  const qs = searchParams?.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
function decodePath(path: string | null): string | null {
  if (!path) return null;
  try {
    return decodeURI(path);
  } catch {
    return path;
  }
}
export function useWebengageDataProvider(): Props {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [prevPath, setPrevPath] = useState<string | null>(null);
  const canonicalPath = useMemo(() => buildCanonicalPath(pathname, searchParams), [pathname, searchParams]);
  useEffect(() => {
    if (!canonicalPath) return;
    const storedCurrent = sessionStorage.getItem(StorageKey.CurrentPath);
    const storedPrev = sessionStorage.getItem(StorageKey.PrevPath);
    const hasChanged = storedCurrent !== canonicalPath;
    if (hasChanged) {
      const nextPrev = storedCurrent ?? StorageKey.Base;
      sessionStorage.setItem(StorageKey.PrevPath, nextPrev);
      sessionStorage.setItem(StorageKey.CurrentPath, canonicalPath);
      setPrevPath(decodePath(nextPrev));
    } else {
      setPrevPath(decodePath(storedPrev));
    }
    setPageTitle(document.querySelector('h1')?.innerHTML ?? '');
  }, [canonicalPath]);

  // This is also used in PDP back button, do not change it without check that part
  return {
    pathname: decodePath(canonicalPath),
    prevPath,
    pageTitle
  };
}